import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Building2, Palette, ListChecks, PartyPopper, UserPlus, ArrowRight } from "lucide-react";

const AGENCY_TYPES = [
  { v: "lead_agency", l: "Lead-Agentur" },
  { v: "funnel_agency", l: "Funnel-Agentur" },
  { v: "performance_marketing_agency", l: "Performance Marketing" },
  { v: "full_service_agency", l: "Full-Service-Agentur" },
  { v: "other", l: "Sonstige" },
];

const STEPS = [
  { n: 1, label: "Agenturprofil", icon: Building2 },
  { n: 2, label: "Agenturbranding", icon: Palette },
  { n: 3, label: "Prüfen", icon: ListChecks },
  { n: 4, label: "Fertig", icon: PartyPopper },
];

export default function AgencyOnboarding() {
  const { agency, checkAuth } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: "", agency_slug: "", contact_person: "", email: "", phone: "", website: "", support_email: "", agency_type: "full_service_agency",
  });
  const [branding, setBranding] = useState({ logo_url: "", primary_color: "#f97316", accent_color: "#0f172a" });

  useEffect(() => {
    if (!agency) return;
    // Defer setState to next microtask → vermeidet sync setState-in-effect (R19-Compiler-Hint)
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setProfile(p => ({
        ...p,
        name: agency.name || "",
        agency_slug: agency.agency_slug || "",
        contact_person: agency.contact_person || "",
        email: agency.email || "",
        phone: agency.phone || "",
        website: agency.website || "",
        support_email: agency.support_email || "",
        agency_type: agency.agency_type || "full_service_agency",
      }));
      setBranding(b => ({
        ...b,
        logo_url: agency.logo_url || "",
        primary_color: agency.primary_color || "#f97316",
        accent_color: agency.accent_color || "#0f172a",
      }));
    });
    return () => { cancelled = true; };
  }, [agency]);

  const saveStep1 = async () => {
    if (!profile.name.trim()) { toast.error("Agenturname fehlt"); return false; }
    setSaving(true);
    try {
      const payload = { ...profile, onboarding_step: 2 };
      if (!payload.agency_slug) delete payload.agency_slug; // leeren Slug nicht senden
      await api.patch("/agency/profile", payload);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Fehler beim Speichern");
      return false;
    } finally { setSaving(false); }
  };

  const saveStep2 = async () => {
    setSaving(true);
    try { await api.patch("/agency/profile", { ...branding, onboarding_step: 3 }); return true; }
    catch (e) { toast.error("Fehler beim Speichern"); return false; }
    finally { setSaving(false); }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/agency/onboarding/complete");
      await checkAuth();
      setStep(4);
    } catch (e) { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  const onNext = async () => {
    if (step === 1) { if (await saveStep1()) setStep(2); }
    else if (step === 2) { if (await saveStep2()) setStep(3); }
    else if (step === 3) { await finish(); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="agency-onboarding-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Agentur-Setup</h1>
        <p className="mt-1 text-slate-600">Richten Sie Ihre Agentur ein. Mandanten legen Sie danach separat im Bereich „Mandanten&ldquo; an.</p>
      </header>

      {/* Stepper */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const active = step === s.n;
          const done = step > s.n;
          return (
            <React.Fragment key={s.n}>
              <div data-testid={`step-${s.n}-indicator`} className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-colors ${active ? "bg-violet-600 text-white" : done ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                <span className="text-sm font-medium">{s.label}</span>
              </div>
              {idx < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="step-1-card">
          <h2 className="text-lg font-bold">1 — Agenturprofil</h2>
          <p className="text-sm text-slate-500 -mt-3">Wer ist Ihre Agentur?</p>
          <Field label="Agenturname *"><Input data-testid="ob-agency-name" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} /></Field>
          <Field label="Portal-Slug">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 font-mono">/portal/</span>
                <Input
                  data-testid="ob-agency-slug"
                  value={profile.agency_slug}
                  onChange={e => setProfile(p => ({ ...p, agency_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") }))}
                  placeholder="solar-growth-agentur"
                  className="font-mono flex-1"
                />
              </div>
              <p className="text-[11px] text-slate-400">
                Ihr Kundenportal ist erreichbar unter <span className="font-mono">/portal/{profile.agency_slug || "ihr-slug"}</span>. Nur a-z, 0-9 und Bindestriche.
              </p>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ansprechpartner"><Input data-testid="ob-agency-contact" value={profile.contact_person} onChange={e => setProfile(p => ({ ...p, contact_person: e.target.value }))} /></Field>
            <Field label="E-Mail"><Input data-testid="ob-agency-email" type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} /></Field>
            <Field label="Telefon"><Input data-testid="ob-agency-phone" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} /></Field>
            <Field label="Website"><Input value={profile.website} onChange={e => setProfile(p => ({ ...p, website: e.target.value }))} placeholder="https://…" /></Field>
            <Field label="Support-E-Mail"><Input value={profile.support_email} onChange={e => setProfile(p => ({ ...p, support_email: e.target.value }))} /></Field>
            <Field label="Agenturtyp">
              <Select value={profile.agency_type} onValueChange={v => setProfile(p => ({ ...p, agency_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{AGENCY_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="step-2-card">
          <h2 className="text-lg font-bold">2 — Agenturbranding</h2>
          <p className="text-sm text-slate-500 -mt-3">Wie sieht Ihre Agentur aus? (kann später jederzeit geändert werden)</p>
          <Field label="Logo-URL">
            <div className="flex gap-3 items-center">
              <Input value={branding.logo_url} onChange={e => setBranding(b => ({ ...b, logo_url: e.target.value }))} placeholder="https://…/logo.png" className="flex-1" />
              {branding.logo_url && <img src={branding.logo_url} alt="" className="h-10 max-w-[120px] object-contain bg-white rounded border p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primärfarbe">
              <div className="flex gap-2 items-center">
                <input type="color" value={branding.primary_color} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))} className="h-10 w-12 rounded border cursor-pointer" />
                <Input value={branding.primary_color} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))} className="font-mono flex-1" />
              </div>
            </Field>
            <Field label="Akzentfarbe">
              <div className="flex gap-2 items-center">
                <input type="color" value={branding.accent_color} onChange={e => setBranding(b => ({ ...b, accent_color: e.target.value }))} className="h-10 w-12 rounded border cursor-pointer" />
                <Input value={branding.accent_color} onChange={e => setBranding(b => ({ ...b, accent_color: e.target.value }))} className="font-mono flex-1" />
              </div>
            </Field>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="step-3-card">
          <h2 className="text-lg font-bold">3 — Zusammenfassung</h2>
          <p className="text-sm text-slate-500 -mt-3">Bitte prüfen Sie Ihre Agenturdaten.</p>
          <SummaryBlock title="Agenturprofil" data={[
            ["Name", profile.name], ["Portal-Slug", profile.agency_slug ? `/portal/${profile.agency_slug}` : ""],
            ["Ansprechpartner", profile.contact_person], ["E-Mail", profile.email],
            ["Telefon", profile.phone], ["Website", profile.website], ["Support-E-Mail", profile.support_email],
            ["Typ", AGENCY_TYPES.find(t => t.v === profile.agency_type)?.l],
          ]} />
          <SummaryBlock title="Agenturbranding" data={[
            ["Logo-URL", branding.logo_url], ["Primärfarbe", branding.primary_color], ["Akzentfarbe", branding.accent_color],
          ]} />
          <div className="bg-violet-50 border border-violet-200 rounded p-3 text-xs text-violet-900 flex gap-2">
            <UserPlus className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Im nächsten Schritt schließen wir das Setup ab. Mandanten (Ihre Solarkunden) legen Sie danach im Bereich <span className="font-semibold">Mandanten</span> an — pro Mandant einzeln mit eigenem Branding und Daten.</span>
          </div>
        </Card>
      )}

      {step === 4 && (
        <Card className="p-10 space-y-5 border-emerald-200 bg-emerald-50 text-center" data-testid="step-4-card">
          <PartyPopper className="w-12 h-12 text-emerald-600 mx-auto" />
          <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Agentur eingerichtet 🎉</h2>
          <p className="text-slate-700">Ihr Agenturzugang ist fertig. Jetzt legen Sie Ihren ersten Mandanten an.</p>
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <Button data-testid="finish-create-client" onClick={() => nav("/agency/clients?create=1")} className="bg-orange-600 hover:bg-orange-700 text-white">
              <UserPlus className="w-4 h-4 mr-2" /> Ersten Mandanten anlegen
            </Button>
            <Button data-testid="finish-go-dashboard" variant="outline" onClick={() => nav("/agency/dashboard")}>
              Zum Agentur-Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      )}

      {step < 4 && (
        <div className="flex justify-between">
          <Button data-testid="onboarding-back-btn" variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1 || saving}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <Button data-testid="onboarding-next-btn" onClick={onNext} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white">
            {saving ? "Speichern …" : step === 3 ? "Agentur-Setup abschließen" : (<>Weiter <ChevronRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const SummaryBlock = ({ title, data }) => (
  <div className="border border-slate-200 rounded-lg p-3">
    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{title}</p>
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
      {data.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-slate-500">{k}</dt>
          <dd className="text-slate-900 truncate">{v || <span className="text-slate-300">—</span>}</dd>
        </React.Fragment>
      ))}
    </dl>
  </div>
);
